import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useDigestConfig,
  useDigestRuns,
  useWatchlist,
} from '@/hooks/useDigestAdmin';
import { useTeam } from '@/hooks/useTeams';
import type { DigestSource } from '@/types/digestAdmin';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Browser TZ abbreviation (e.g. "EST" / "EDT").
const TZ_ABBR =
  new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
    .formatToParts(new Date())
    .find((p) => p.type === 'timeZoneName')?.value ?? 'local';

const TIME_FMT = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

export default function DigestDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: configResp, isLoading, error } = useDigestConfig(id);
  const { data: runsResp } = useDigestRuns({ config_id: id, limit: 10 });

  const config = configResp?.data;
  const sources = config?.sources ?? [];
  const { data: team } = useTeam(config?.owner_id);

  const directSlugs = useMemo(
    () =>
      sources
        .filter((s) => s.source_type === 'slug' && s.slug)
        .map((s) => s.slug as string),
    [sources]
  );

  const cadenceLabel = (() => {
    if (!config) return '';
    if (config.cadence === 'weekly' && config.day_of_week !== null && config.time_of_day) {
      const [hh = 0, mm = 0] = config.time_of_day.split(':').map(Number);
      const utcDate = new Date(Date.UTC(2024, 0, 7 + config.day_of_week, hh, mm));
      const dow = DOW[utcDate.getDay()];
      return `Every ${dow} at ${TIME_FMT.format(utcDate)} ${TZ_ABBR}`;
    }
    if (config.cadence === 'monthly' && config.day_of_month !== null && config.time_of_day) {
      const [hh = 0, mm = 0] = config.time_of_day.split(':').map(Number);
      const utcDate = new Date(Date.UTC(2024, 0, config.day_of_month, hh, mm));
      const dom = utcDate.getDate();
      const suffix =
        dom % 100 >= 11 && dom % 100 <= 13
          ? 'th'
          : dom % 10 === 1
            ? 'st'
            : dom % 10 === 2
              ? 'nd'
              : dom % 10 === 3
                ? 'rd'
                : 'th';
      return `Monthly on the ${dom}${suffix} at ${TIME_FMT.format(utcDate)} ${TZ_ABBR}`;
    }
    return config.cadence;
  })();

  if (isLoading) {
    return <div className="container mx-auto p-6 text-gray-500">Loading…</div>;
  }
  if (error || !config) {
    return (
      <div className="container mx-auto p-6 text-red-600">
        {error ? (error as Error).message : 'Digest config not found.'}
        <div className="mt-4">
          <Link to="/digests" className="text-blue-600 hover:underline">
            ← Back to digests
          </Link>
        </div>
      </div>
    );
  }

  const watchlistSources = sources.filter(
    (s) => s.source_type === 'watchlist' && s.watchlist_id
  );

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <Link to="/digests" className="text-sm text-blue-600 hover:underline">
          ← Back to digests
        </Link>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{config.name}</CardTitle>
            <div className="flex gap-2">
              <Badge variant={config.enabled ? 'default' : 'secondary'}>
                {config.enabled ? 'enabled' : 'disabled'}
              </Badge>
              {config.realtime_enabled && <Badge variant="outline">realtime</Badge>}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div>
            <span className="text-gray-500">Team:</span>{' '}
            {team ? (
              <>
                <span className="font-medium">{team.name}</span>{' '}
                <span className="text-xs text-gray-400">({team.slug})</span>
              </>
            ) : (
              <span className="font-mono text-xs text-gray-400">{config.owner_id}</span>
            )}
          </div>
          <div>
            <span className="text-gray-500">Cadence:</span> {cadenceLabel}
            {config.realtime_enabled && (
              <> · also fires realtime on diff publish</>
            )}
          </div>
          <div>
            <span className="text-gray-500">Recipients:</span>{' '}
            {config.recipients?.length ? (
              <span className="font-mono text-xs">
                {config.recipients.join(', ')}
              </span>
            ) : (
              <span className="text-gray-400">none</span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sources ({sources.length})</CardTitle>
          <p className="text-sm text-gray-500">
            What feeds this digest. Watchlists expand to their member pages below.
          </p>
        </CardHeader>
        <CardContent>
          {sources.length === 0 ? (
            <div className="text-sm text-gray-400">No sources attached.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Section label</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sources.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>
                      <Badge variant="outline">{s.source_type}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {s.source_type === 'watchlist'
                        ? s.watchlist?.name ?? s.watchlist_id
                        : s.slug}
                    </TableCell>
                    <TableCell className="text-xs">
                      {s.section_label ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Companies covered</CardTitle>
          <p className="text-sm text-gray-500">
            Every company that could appear in this digest, joined from watchlist pages
            and direct slug sources.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {watchlistSources.map((s) => (
            <WatchlistCompaniesPanel key={s.id} source={s} />
          ))}
          {directSlugs.length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-700 mb-2">
                Direct slugs ({directSlugs.length})
              </div>
              <div className="flex flex-wrap gap-1">
                {directSlugs.map((slug) => (
                  <Link
                    key={slug}
                    to={`/companies/${slug}`}
                    className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                  >
                    {slug}
                  </Link>
                ))}
              </div>
            </div>
          )}
          {watchlistSources.length === 0 && directSlugs.length === 0 && (
            <div className="text-sm text-gray-400">
              No companies — this digest has no sources.
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent runs</CardTitle>
          <p className="text-sm text-gray-500">Last 10 evaluate/send decisions.</p>
        </CardHeader>
        <CardContent>
          {!runsResp?.runs.length ? (
            <div className="text-sm text-gray-400">No runs recorded yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Period / Diff</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runsResp.runs.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs whitespace-nowrap">
                      {format(new Date(r.started_at), 'yyyy-MM-dd HH:mm')}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{r.trigger}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          r.status === 'sent'
                            ? 'default'
                            : r.status === 'failed'
                              ? 'destructive'
                              : 'secondary'
                        }
                      >
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {r.period ?? (r.diff_id ? r.diff_id.slice(0, 8) : '—')}
                    </TableCell>
                    <TableCell className="text-xs">{r.recipients_sent ?? 0}</TableCell>
                    <TableCell className="text-xs text-gray-600 max-w-xs truncate">
                      {r.skip_reason || r.error || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Renders one watchlist source's companies inline — fetches its pages and
// shows a compact grid.
function WatchlistCompaniesPanel({ source }: { source: DigestSource }) {
  const { data, isLoading } = useWatchlist(source.watchlist_id ?? undefined);
  const pages = data?.data?.pages ?? [];

  return (
    <div>
      <div className="text-xs font-medium text-gray-700 mb-2">
        Watchlist:{' '}
        <span className="font-mono">
          {source.watchlist?.name ?? source.watchlist_id?.slice(0, 8)}
        </span>{' '}
        <span className="text-gray-400">({pages.length} pages)</span>
      </div>
      {isLoading ? (
        <div className="text-xs text-gray-500">Loading…</div>
      ) : pages.length === 0 ? (
        <div className="text-xs text-gray-400">No pages.</div>
      ) : (
        <div className="flex flex-wrap gap-1">
          {pages.map((wp) => {
            const c = wp.page?.company;
            if (!c) return null;
            return (
              <Link
                key={wp.id}
                to={`/companies/${c.slug}`}
                className="text-xs px-2 py-1 border rounded hover:bg-gray-50 flex items-center gap-1"
              >
                {c.logo_url && (
                  <img src={c.logo_url} alt="" className="w-3 h-3 rounded-sm" />
                )}
                {c.slug}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
